import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import { ElementRef, forwardRef, ReactNode } from 'react';
import { baseVariantStyles, variants } from './base';
import { TabsContext, useTabsContext } from './context';

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

export type TabsProps = TabsPrimitive.TabsProps &
  VariantProps<typeof baseVariantStyles> & {
    children: ReactNode;
  };

/**
 * @example: Tabs can be used as a controlled component, but it can also function without local state:
 * <Tabs.Root defaultValue="tab1" variant="contained">
 *   <Tabs.List>
 *     <Tabs.Trigger value="tab1" title="Tab 1" />
 *     <Tabs.Trigger value="tab2" title="Tab 2" />
 *   </Tabs.List>
 *
 *   <Tabs.Content value="tab1">...</Tabs.Content>
 *   <Tabs.Content value="tab2">...</Tabs.Content>
 * </Tabs.Root>
 *
 * ...where the `value` prop is tracked by Radix and determines which tab (and corresponding content) is selected/active.
 * `defaultValue` determines which tab is selected/active by default. see the Radix Tabs primitive source code for more info.
 *
 * at the page level, prefer the use of routes for tab content.
 *
 */
// eslint-disable-next-line react-x/no-forward-ref -- TODO: Wait! Don't change this file without seeing if you can remove this disable. It was added during a bulk version update, but fixing it would leave the park nicer than you found it.
const TabsRoot = forwardRef<ElementRef<typeof TabsPrimitive.Root>, TabsProps>(
  ({ children, variant = 'basic', ...props }, ref) => {
    return (
      <TabsContext value={{ variant }}>
        <TabsPrimitive.Root ref={ref} {...props}>
          {children}
        </TabsPrimitive.Root>
      </TabsContext>
    );
  },
);

TabsRoot.displayName = 'Tabs';

/* -------------------------------------------------------------------------------------------------
 * Triggers/Tabs List
 * -----------------------------------------------------------------------------------------------*/

const tabsList = cva('flex shrink-0', {
  variants: {
    variant: {
      basic: '',
      contained: '',
      pills: 'w-full gap-x-xxs rounded-circle bg-neutral-subtle p-xxs',
    },
  },
  defaultVariants: { variant: 'basic' },
});

type TabsListProps = TabsPrimitive.TabsListProps &
  // variant is read from context (see TabsRoot), not settable per-List -
  // omitted so it isn't typed as a prop and spread through to the DOM.
  Omit<VariantProps<typeof tabsList>, 'variant'>;

// eslint-disable-next-line react-x/no-forward-ref -- TODO: Wait! Don't change this file without seeing if you can remove this disable. It was added during a bulk version update, but fixing it would leave the park nicer than you found it.
const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ children, ...props }, ref) => {
  const { variant } = useTabsContext();

  return (
    <TabsPrimitive.List ref={ref} className={tabsList({ variant })} {...props}>
      {children}
    </TabsPrimitive.List>
  );
});

TabsList.displayName = 'TabsList';

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

const tabTrigger = cva(
  'group flex flex-1 items-center justify-center gap-x-xs cursor-pointer whitespace-nowrap transition-colors select-none hover:bg-surface-hover',
  { variants: { ...variants } },
);

type TabTriggerProps = TabsPrimitive.TabsTriggerProps & {
  title: string;
  value: string;
  // e.g. for the 'pills' variant, where each tab is commonly icon + label.
  // The trigger carries a `group` class, so pass a color that reacts to
  // active state the same way the variants above do, e.g.
  // `color="text-content-subdued group-data-[state=active]:text-content-default"`
  // - Icon sets its own color directly, so it won't otherwise pick up the
  // trigger's own active-state color the way the plain-text label does.
  leadingIcon?: ReactNode;
};

// eslint-disable-next-line react-x/no-forward-ref -- TODO: Wait! Don't change this file without seeing if you can remove this disable. It was added during a bulk version update, but fixing it would leave the park nicer than you found it.
const TabTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  TabTriggerProps
>(({ title, leadingIcon, disabled = false, ...props }, ref) => {
  const { variant } = useTabsContext();

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      disabled={disabled}
      className={tabTrigger({ variant })}
      {...props}
    >
      {leadingIcon}
      <span className="text-body-s">{title}</span>
    </TabsPrimitive.Trigger>
  );
});

TabTrigger.displayName = 'TabTrigger';

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

const tabContent = cva('');

type TabContentProps = TabsPrimitive.TabsContentProps &
  VariantProps<typeof tabContent> & { children: ReactNode };

// eslint-disable-next-line react-x/no-forward-ref -- TODO: Wait! Don't change this file without seeing if you can remove this disable. It was added during a bulk version update, but fixing it would leave the park nicer than you found it.
const TabContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  TabContentProps
>(({ children, ...props }, ref) => {
  return (
    <TabsPrimitive.Content ref={ref} className={tabContent()} {...props}>
      {children}
    </TabsPrimitive.Content>
  );
});

TabContent.displayName = 'TabContent';

export {
  TabContent as Content,
  TabsList as List,
  TabsRoot as Root,
  TabTrigger as Trigger,
};
